import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Building2,
  Network,
  Plug,
  Users,
  Zap,
  Globe2,
  Activity,
  Workflow,
  AlertTriangle,
  ShieldCheck,
  Layers,
  BarChart3,
  ArrowUpRight,
  Cpu,
  HeartPulse,
  Briefcase,
  Anchor,
  Quote,
} from "lucide-react";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";
import { SEO } from "@/components/SEO";

/* ───────────────────────────── Data ───────────────────────────── */
const challenges = [
  "Fragmented bancassurance operations",
  "Slow onboarding of banks and partners",
  "Manual intermediary workflows",
  "Limited personalization capabilities",
  "Disconnected embedded journeys",
  "Lack of real-time issuance and servicing",
  "Poor visibility across partner ecosystems",
];

const metrics = [
  {
    value: "220+",
    label: "Bancassurance Relationships Managed",
    detail:
      "Highly automated bank relationship management and partner servicing at scale.",
    Icon: Building2,
  },
  {
    value: "7",
    label: "Large-Scale Social Security Programs",
    detail:
      "Including implementation support for India's largest social insurance initiatives.",
    Icon: ShieldCheck,
  },
  {
    value: "Real-Time",
    label: "Policy Issuance & Claims Intimation",
    detail:
      "Integrated issuance, servicing, and claims workflows across banking and partner ecosystems.",
    Icon: Zap,
  },
  {
    value: "Days",
    label: "To Launch Personalized Digital Channels",
    detail:
      "AI-assisted configuration-driven setup with minimal operational dependency.",
    Icon: Sparkles,
  },
];

const impactBlocks = [
  {
    Icon: Building2,
    title: "Embedded Bancassurance",
    description:
      "Enabled insurers and banks to launch integrated digital insurance journeys directly within banking ecosystems and customer workflows.",
  },
  {
    Icon: Sparkles,
    title: "Personalized Digital Channels",
    description:
      "Delivered AI-driven personalized insurance journeys based on customer segmentation, behavioral analytics, and product affinity.",
  },
  {
    Icon: Network,
    title: "Multi-Access Distribution",
    description:
      "Unified brokers, intermediaries, mutual clients, banks, relationship managers, and customers through collaborative digital workflows.",
  },
  {
    Icon: Activity,
    title: "Real-Time Insurance Operations",
    description:
      "Enabled real-time quotation, issuance, claims initiation, servicing, and operational notifications across partner ecosystems.",
  },
];

const successStories = [
  {
    Icon: HeartPulse,
    customer: "Leading Indian Health Insurer",
    outcome:
      "Enabled multi-bank integrated health insurance distribution with real-time issuance, optional benefit structures, and embedded credit-protection journeys.",
  },
  {
    Icon: Building2,
    customer: "Leading Private Sector Banks",
    outcome:
      "Delivered relationship-manager-driven digital sales journeys with quotation, personalized product selection, and real-time policy issuance.",
  },
  {
    Icon: Briefcase,
    customer: "Leading General Insurer",
    outcome:
      "Built personalized digital channels delivering significant annual premium growth across high-value customer ecosystems.",
  },
  {
    Icon: Anchor,
    customer: "Prestige Assurance, Nigeria",
    outcome:
      "Scaled digital marine cargo insurance operations from launch to significant monthly premium volumes through fully digital intermediary enablement.",
  },
];

const capabilities = [
  "Bancassurance",
  "Embedded Insurance",
  "Digital Ecosystems",
  "Partner Portals",
  "Relationship Manager Journeys",
  "Intermediary Workflows",
  "Real-Time Issuance",
  "Claims Automation",
  "Behavioral Analytics",
  "AI Recommendations",
  "Personalized Digital Channels",
  "API-Led Distribution",
];

/* Ecosystem nodes for hero visual */
const ECOSYSTEM_NODES = [
  { label: "Banks", angle: -90, Icon: Building2 },
  { label: "Brokers", angle: -30, Icon: Briefcase },
  { label: "Embedded", angle: 30, Icon: Plug },
  { label: "Affinity", angle: 90, Icon: Users },
  { label: "Digital", angle: 150, Icon: Globe2 },
  { label: "RM", angle: -150, Icon: Network },
];

/* ───────────────────────────── Page ───────────────────────────── */
export default function GripCaseStudy() {
  return (
    <main className="bg-white text-ink" data-testid="mfinsure-grip-case-study">
      <SEO
        title="Case Study — Scaling Bancassurance & Embedded Insurance with MFInsure® GRIP"
        description="How insurers and banks scaled bancassurance, embedded insurance and digital ecosystems with Gradatim MFInsure® GRIP — 220+ banking relationships, real-time issuance, days-to-launch new channels."
        path="/insights/mfinsure-grip"
      />
      <Nav />

      {/* HERO — deep navy with ecosystem visual */}
      <section
        data-nav-dark
        className="relative overflow-hidden pt-32 md:pt-40 pb-20 md:pb-28 grain-overlay"
        style={{ background: "#0B1F3A" }}
      >
        {/* Grid texture */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            maskImage:
              "radial-gradient(ellipse at center, rgba(0,0,0,0.7), transparent 80%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, rgba(0,0,0,0.7), transparent 80%)",
          }}
        />
        {/* Ambient copper + cyan glows */}
        <div
          aria-hidden
          className="absolute -top-40 right-1/4 h-[480px] w-[480px] rounded-full bg-copper/15 blur-3xl"
        />
        <div
          aria-hidden
          className="absolute -bottom-40 left-1/4 h-[420px] w-[420px] rounded-full bg-blue-500/10 blur-3xl"
        />

        <div className="relative max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* LEFT — text */}
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-copper/30 bg-copper/10"
                data-testid="grip-case-study-badge"
              >
                <Sparkles className="h-3.5 w-3.5 text-copper" strokeWidth={2} />
                <span className="overline text-copper">
                  Embedded Distribution & Ecosystem Growth
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="font-display font-medium text-4xl sm:text-5xl md:text-[56px] lg:text-[60px] tracking-tight leading-[1.05] text-white mt-7"
                data-testid="grip-case-study-title"
              >
                Scaling Bancassurance, Embedded Insurance & Digital Ecosystems
                <br className="hidden md:block" />{" "}
                with <span className="text-copper">MFInsure® GRIP.</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
                className="text-base md:text-[17.5px] text-slate-300 leading-relaxed mt-7 max-w-[680px]"
              >
                Gradatim MFInsure® GRIP enables insurers, banks, intermediaries,
                and ecosystem partners to launch, manage, and scale digital
                insurance journeys across bancassurance, embedded insurance,
                affinity distribution, and personalized digital channels.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.26, ease: [0.16, 1, 0.3, 1] }}
                className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3 text-[12.5px] text-slate-400"
              >
                <span className="inline-flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-copper" />
                  Bancassurance
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-copper" />
                  Embedded
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-copper" />
                  Affinity
                </span>
                <span className="inline-flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-copper" />
                  Digital Ecosystems
                </span>
              </motion.div>
            </div>

            {/* RIGHT — ecosystem visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-5 hidden lg:block"
            >
              <EcosystemVisual />
            </motion.div>
          </div>
        </div>
      </section>

      {/* BUSINESS CONTEXT */}
      <section className="bg-white border-b border-hairline">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-5"
              data-testid="business-context-block"
            >
              <div className="overline text-copper mb-4">Business Context</div>
              <h2 className="font-display text-2xl md:text-[28px] lg:text-[32px] tracking-tight leading-[1.15] text-ink">
                Insurers, banks & ecosystem partners.
              </h2>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.55, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-7 lg:pt-2"
            >
              <p className="text-base md:text-[17px] text-slate-600 leading-relaxed">
                Leading insurers and financial institutions required a scalable
                digital ecosystem platform to support bancassurance growth,
                embedded insurance journeys, intermediary enablement,
                personalized digital experiences, and high-volume partner-driven
                insurance distribution.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CHALLENGE ↔ SOLUTION */}
      <section className="bg-cream">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Challenge */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-2xl bg-white p-7 md:p-9 border border-hairline"
              style={{ boxShadow: "0 8px 28px -12px rgba(11,31,58,0.10)" }}
              data-testid="challenge-block"
            >
              <div className="flex items-center gap-3 mb-7">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-copper/10 border border-copper/20">
                  <AlertTriangle
                    className="h-4 w-4 text-copper"
                    strokeWidth={1.8}
                  />
                </span>
                <div>
                  <div className="overline text-slate-500">The Challenge</div>
                  <h3 className="font-display text-xl tracking-tight text-ink">
                    Distribution Challenges
                  </h3>
                </div>
              </div>
              <ul className="space-y-3.5">
                {challenges.map((c) => (
                  <li
                    key={c}
                    className="flex items-start gap-3 pb-3.5 border-b last:border-0 last:pb-0"
                    style={{ borderColor: "rgba(11,31,58,0.06)" }}
                  >
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-copper shrink-0" />
                    <span className="text-[14.5px] text-slate-600 leading-relaxed">
                      {c}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Solution — radial growth feel */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.55, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
              className="relative rounded-2xl p-7 md:p-9 overflow-hidden grain-overlay"
              style={{
                background:
                  "linear-gradient(135deg, #0B1F3A 0%, #133057 60%, #0B1F3A 100%)",
                boxShadow: "0 20px 60px -20px rgba(11,31,58,0.45)",
              }}
              data-testid="solution-block"
            >
              <div
                aria-hidden
                className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-copper/15 blur-3xl"
              />
              <div
                aria-hidden
                className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-blue-400/10 blur-3xl"
              />
              <div className="relative flex items-center gap-3 mb-7">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-copper/20 border border-copper/30">
                  <ShieldCheck
                    className="h-4 w-4 text-copper"
                    strokeWidth={1.8}
                  />
                </span>
                <div>
                  <div className="overline text-copper">The Solution</div>
                  <h3 className="font-display text-xl tracking-tight text-white">
                    Gradatim GRIP Transformation
                  </h3>
                </div>
              </div>
              <p className="relative text-[15px] md:text-base leading-relaxed text-slate-300">
                MFInsure® GRIP unified partner onboarding, bancassurance
                management, embedded insurance journeys, personalized digital
                channels, intermediary workflows, real-time issuance, customer
                servicing, and ecosystem orchestration into a configurable
                digital insurance growth platform.
              </p>
              <div className="relative mt-7 grid grid-cols-3 gap-3">
                {["Configurable", "Connected", "Real-Time"].map((tag) => (
                  <div
                    key={tag}
                    className="text-center py-2.5 rounded-md border border-white/10 bg-white/5 backdrop-blur-sm"
                  >
                    <span className="text-[11.5px] uppercase tracking-[0.2em] text-white/80">
                      {tag}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HERO METRICS — connected horizontal flow */}
      <section className="relative bg-white overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(200,75,49,0.08), transparent 70%)",
          }}
        />
        <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="text-center max-w-2xl mx-auto mb-14 md:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-copper/20 bg-copper/5"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-copper" />
              <span className="overline text-copper">
                Ecosystem Scale, Delivered
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-medium text-3xl sm:text-4xl md:text-[48px] tracking-tight leading-[1.05] text-ink mt-6"
              data-testid="metrics-headline"
            >
              Growth that scales with the{" "}
              <span className="text-copper">network.</span>
            </motion.h2>
          </div>

          {/* Connected metric flow */}
          <div className="relative" data-testid="metric-cards">
            {/* Horizontal flow line (desktop) */}
            <div
              aria-hidden
              className="hidden lg:block absolute left-0 right-0 top-[56px] h-px pointer-events-none"
              style={{
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(200,75,49,0.4) 12%, rgba(200,75,49,0.4) 88%, transparent 100%)",
              }}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {metrics.map((m, i) => (
                <motion.div
                  key={m.label}
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.05 }}
                  transition={{
                    duration: 0.55,
                    delay: 0.04 + i * 0.06,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  whileHover={{ y: -4 }}
                  className="relative group"
                  data-testid={`metric-card-${i + 1}`}
                >
                  {/* Node dot on flow line */}
                  <div
                    aria-hidden
                    className="hidden lg:flex absolute left-1/2 -translate-x-1/2 top-[56px] -translate-y-1/2 z-20 items-center justify-center"
                  >
                    <span
                      className="block h-3 w-3 rounded-full bg-white border-2 border-copper"
                      style={{ boxShadow: "0 0 14px rgba(200,75,49,0.4)" }}
                    />
                  </div>

                  <div
                    className="relative rounded-2xl bg-white p-7 md:p-8 border border-hairline transition-shadow group-hover:shadow-lg"
                    style={{ boxShadow: "0 4px 20px -8px rgba(11,31,58,0.08)" }}
                  >
                    <div className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-copper/10 border border-copper/20 mb-6">
                      <m.Icon
                        className="h-4 w-4 text-copper"
                        strokeWidth={1.8}
                      />
                    </div>
                    <div className="font-display font-medium text-4xl md:text-5xl lg:text-[52px] tracking-tight leading-none text-ink">
                      {m.value}
                    </div>
                    <div className="mt-4 text-[13.5px] md:text-[14px] text-slate-700 font-medium leading-snug">
                      {m.label}
                    </div>
                    {m.detail && (
                      <div className="mt-4 pt-4 border-t border-hairline">
                        <p className="text-[12.5px] text-slate-500 leading-relaxed">
                          {m.detail}
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DIGITAL ECOSYSTEM IMPACT */}
      <section className="bg-cream border-y border-hairline">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="text-center max-w-2xl mx-auto mb-14 md:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-copper/20 bg-white"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-copper" />
              <span className="overline text-copper">
                Digital Distribution Impact
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-medium text-3xl sm:text-4xl md:text-[44px] tracking-tight leading-[1.08] text-ink mt-6"
              data-testid="impact-headline"
            >
              Distribution, <span className="text-copper">reimagined.</span>
            </motion.h2>
          </div>

          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6"
            data-testid="impact-blocks"
          >
            {impactBlocks.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{
                  duration: 0.55,
                  delay: 0.04 + i * 0.06,
                  ease: [0.16, 1, 0.3, 1],
                }}
                whileHover={{ y: -3 }}
                className="group relative rounded-2xl bg-white p-7 md:p-8 border border-hairline transition-all hover:shadow-md overflow-hidden"
                data-testid={`impact-block-${i + 1}`}
              >
                {/* Diagonal flow accent */}
                <div
                  aria-hidden
                  className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-copper/8 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity"
                />
                <div
                  aria-hidden
                  className="absolute inset-x-0 top-0 h-px opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent 0%, rgba(200,75,49,0.6) 50%, transparent 100%)",
                  }}
                />
                <div className="relative flex items-start gap-5">
                  <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-cream border border-hairline shrink-0 group-hover:border-copper/40 group-hover:bg-white transition-colors">
                    <b.Icon className="h-5 w-5 text-copper" strokeWidth={1.8} />
                  </span>
                  <div>
                    <h3 className="font-display text-[20px] md:text-[22px] tracking-tight text-ink mb-3 leading-tight">
                      {b.title}
                    </h3>
                    <p className="text-[14px] md:text-[14.5px] text-slate-600 leading-relaxed">
                      {b.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOMER SUCCESS HIGHLIGHTS — horizontal cards */}
      <section className="bg-white">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-16">
            <div className="max-w-2xl">
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-copper/20 bg-copper/5"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-copper" />
                <span className="overline text-copper">
                  Customer Success Highlights
                </span>
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
                className="font-display font-medium text-3xl sm:text-4xl md:text-[44px] tracking-tight leading-[1.08] text-ink mt-6"
                data-testid="success-headline"
              >
                Built with partners, <span className="text-copper">at scale.</span>
              </motion.h2>
            </div>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="text-[14.5px] text-slate-600 leading-relaxed max-w-md md:text-right"
            >
              Four distinct ecosystems — health, banking, general insurance and
              international — running on the same growth platform.
            </motion.p>
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5"
            data-testid="success-cards"
          >
            {successStories.map((s, i) => (
              <motion.article
                key={s.customer}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{
                  duration: 0.55,
                  delay: 0.04 + i * 0.06,
                  ease: [0.16, 1, 0.3, 1],
                }}
                whileHover={{ y: -4 }}
                className="group relative rounded-2xl bg-cream border border-hairline p-7 md:p-8 transition-all hover:bg-white hover:shadow-lg overflow-hidden"
                data-testid={`success-card-${i + 1}`}
              >
                <div
                  aria-hidden
                  className="absolute inset-x-0 top-0 h-px opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent 0%, rgba(200,75,49,0.6) 50%, transparent 100%)",
                  }}
                />
                {/* Card index */}
                <div className="flex items-center justify-between mb-6">
                  <span className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-white border border-hairline group-hover:border-copper/40 transition-colors">
                    <s.Icon className="h-4.5 w-4.5 text-copper" strokeWidth={1.8} />
                  </span>
                  <span className="text-[11px] font-mono tracking-[0.18em] text-slate-400">
                    0{i + 1} / 04
                  </span>
                </div>
                <div className="overline text-slate-500 mb-3">Customer</div>
                <h3 className="font-display text-[17px] md:text-[19px] tracking-tight text-ink leading-tight mb-4">
                  {s.customer}
                </h3>
                <div className="pt-4 border-t border-hairline">
                  <p className="text-[13.5px] text-slate-600 leading-relaxed">
                    {s.outcome}
                  </p>
                </div>
                <div className="mt-5 inline-flex items-center gap-1.5 text-copper text-[12px] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  <span>Outcome delivered</span>
                  <ArrowUpRight className="h-3 w-3" />
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITY STRIP */}
      <section className="bg-cream border-t border-hairline">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="flex items-center justify-center gap-3 mb-9">
            <span className="h-px w-12 bg-hairline" />
            <span className="overline text-slate-500">
              Platform Capabilities
            </span>
            <span className="h-px w-12 bg-hairline" />
          </div>
          <div
            className="flex flex-wrap items-center justify-center gap-2.5 md:gap-3"
            data-testid="capability-strip"
          >
            {capabilities.map((c, i) => (
              <motion.div
                key={c}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{
                  duration: 0.4,
                  delay: 0.02 + i * 0.025,
                  ease: [0.16, 1, 0.3, 1],
                }}
                whileHover={{ y: -2 }}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-hairline hover:border-copper/40 hover:shadow-sm transition-all"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-copper" />
                <span className="text-[12.5px] font-medium tracking-tight text-ink">
                  {c}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STRATEGIC ENDING — pull quote */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[24px] md:rounded-[32px] overflow-hidden grain-overlay px-8 md:px-16 py-16 md:py-24"
            style={{
              background:
                "linear-gradient(135deg, #0B1F3A 0%, #133057 50%, #0B1F3A 100%)",
            }}
            data-testid="strategic-ending"
          >
            <div
              aria-hidden
              className="absolute -top-40 -right-40 h-[400px] w-[400px] rounded-full bg-copper/15 blur-3xl"
            />
            <div
              aria-hidden
              className="absolute -bottom-40 -left-40 h-[400px] w-[400px] rounded-full bg-blue-500/10 blur-3xl"
            />
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.05] pointer-events-none"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
                backgroundSize: "64px 64px",
              }}
            />

            <div className="relative max-w-[920px] mx-auto text-center">
              <Quote className="h-10 w-10 text-copper mx-auto mb-7" strokeWidth={1.5} />
              <p className="font-display text-2xl sm:text-3xl md:text-[40px] leading-[1.2] tracking-tight text-white">
                MFInsure® GRIP transforms insurance distribution into a{" "}
                <span className="text-copper">
                  scalable, intelligent, partner-driven digital ecosystem.
                </span>
              </p>

              <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                <Link
                  to="/#contact"
                  data-testid="closing-cta-primary"
                  className="group inline-flex items-center gap-3 bg-copper hover:bg-copper-600 text-white px-7 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
                >
                  Request a demo
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/products/grip"
                  data-testid="closing-cta-secondary"
                  className="group inline-flex items-center gap-3 border border-white/30 hover:border-copper text-white hover:text-copper px-7 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
                >
                  Explore MFInsure® GRIP
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   EcosystemVisual — orbital partners connecting to the GRIP center
   ───────────────────────────────────────────────────────────────────── */
const EcosystemVisual = () => {
  const size = 460;
  const center = size / 2;
  const radius = 175;
  return (
    <div
      className="relative mx-auto"
      style={{ width: size, height: size, maxWidth: "100%" }}
      data-testid="grip-ecosystem-visual"
    >
      {/* Orbit rings */}
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="absolute inset-0 w-full h-full"
        aria-hidden
      >
        <defs>
          <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#C84B31" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#C84B31" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="connector" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#C84B31" stopOpacity="0" />
            <stop offset="50%" stopColor="#C84B31" stopOpacity="0.65" />
            <stop offset="100%" stopColor="#C84B31" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Outer rings */}
        <circle
          cx={center}
          cy={center}
          r={radius + 28}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth="1"
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="1"
          strokeDasharray="3 5"
        />
        <circle
          cx={center}
          cy={center}
          r={radius - 50}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth="1"
        />

        {/* Center glow */}
        <circle cx={center} cy={center} r="100" fill="url(#centerGlow)" />

        {/* Connector lines from center to each node */}
        {ECOSYSTEM_NODES.map((n, i) => {
          const rad = (n.angle * Math.PI) / 180;
          const x = center + radius * Math.cos(rad);
          const y = center + radius * Math.sin(rad);
          return (
            <line
              key={`line-${n.label}`}
              x1={center}
              y1={center}
              x2={x}
              y2={y}
              stroke="url(#connector)"
              strokeWidth="1.2"
            />
          );
        })}

        {/* Animated traveling pulses along select lines */}
        {ECOSYSTEM_NODES.slice(0, 3).map((n, i) => {
          const rad = (n.angle * Math.PI) / 180;
          const x = center + radius * Math.cos(rad);
          const y = center + radius * Math.sin(rad);
          return (
            <motion.circle
              key={`pulse-${n.label}`}
              r="3"
              fill="#C84B31"
              initial={{ cx: center, cy: center, opacity: 0 }}
              animate={{
                cx: [center, x, center],
                cy: [center, y, center],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 3.8,
                delay: i * 1.1,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              style={{ filter: "drop-shadow(0 0 6px #C84B31)" }}
            />
          );
        })}
      </svg>

      {/* Center node — GRIP */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl px-5 py-4 backdrop-blur-md"
        style={{
          background:
            "linear-gradient(135deg, rgba(200,75,49,0.20) 0%, rgba(200,75,49,0.08) 100%)",
          border: "1px solid rgba(200,75,49,0.5)",
          boxShadow: "0 0 40px rgba(200,75,49,0.35)",
        }}
      >
        <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-copper text-center">
          Gradatim
        </div>
        <div className="font-display text-xl text-white tracking-tight text-center mt-1">
          MFInsure® GRIP
        </div>
        <div className="flex items-center justify-center gap-1.5 mt-2">
          <span
            className="h-1.5 w-1.5 rounded-full bg-copper animate-pulse"
            style={{ boxShadow: "0 0 8px #C84B31" }}
          />
          <span className="text-[10px] uppercase tracking-[0.2em] text-white/60">
            Active
          </span>
        </div>
      </div>

      {/* Partner nodes */}
      {ECOSYSTEM_NODES.map((n, i) => {
        const rad = (n.angle * Math.PI) / 180;
        const x = center + radius * Math.cos(rad);
        const y = center + radius * Math.sin(rad);
        const NodeIcon = n.Icon;
        return (
          <motion.div
            key={n.label}
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: 0.5,
              delay: 0.3 + i * 0.08,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5"
            style={{ left: x, top: y }}
          >
            <span
              className="inline-flex items-center justify-center h-10 w-10 rounded-xl backdrop-blur-md"
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.18)",
                boxShadow: "0 4px 14px rgba(0,0,0,0.3)",
              }}
            >
              <NodeIcon className="h-4 w-4 text-copper" strokeWidth={1.8} />
            </span>
            <span className="text-[10.5px] font-medium uppercase tracking-[0.15em] text-white/85">
              {n.label}
            </span>
          </motion.div>
        );
      })}
    </div>
  );
};
